import * as React from 'react';
import SearchField from './SearchField';
import { useEffect, useRef, useState } from 'react';

/*
 * FilterMenu — the Visitors-style structured filter: a filter-icon
 * trigger opens a category list ("Filter by"); choosing a category
 * drills into a searchable value list; picking a value applies an
 * `<dimension> is <value>` filter. Applied filters render as dashed
 * chips next to the trigger (see ActivationsV2). Dimensions are
 * data-driven so new ones (tags, status, …) plug in as endpoints land.
 */

export interface FilterDimension {
    key: string;
    label: string;
    // Required: every dimension row shows its icon (and chevron) in the menu.
    icon: React.ReactNode;
    values: string[];
}

export interface ActiveFilter {
    key: string;
    label: string;
    value: string;
}

interface FilterMenuProps {
    dimensions: FilterDimension[];
    onAdd: (filter: ActiveFilter) => void;
}

export default function FilterMenu({ dimensions, onAdd }: FilterMenuProps) {
    const [open, setOpen] = useState(false);
    const [drill, setDrill] = useState<FilterDimension | null>(null);
    const [query, setQuery] = useState('');
    const rootRef = useRef<HTMLDivElement>(null);

    const close = () => {
        setOpen(false);
        setDrill(null);
        setQuery('');
    };

    useEffect(() => {
        if (!open) return;
        const onDown = (e: MouseEvent) => {
            if (!rootRef.current?.contains(e.target as Node)) close();
        };
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') close();
        };
        document.addEventListener('mousedown', onDown);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDown);
            document.removeEventListener('keydown', onKey);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const shownValues = drill
        ? drill.values.filter((v) =>
              v.toLowerCase().includes(query.trim().toLowerCase()),
          )
        : [];

    return (
        <div ref={rootRef} className="relative">
            <button
                type="button"
                title="Filter"
                aria-haspopup="menu"
                aria-expanded={open}
                onClick={() => (open ? close() : setOpen(true))}
                className="ps-pill flex w-9 cursor-pointer items-center justify-center text-muted-foreground hover:text-foreground"
            >
                <svg
                    width="15"
                    height="15"
                    viewBox="0 0 16 16"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                >
                    <path d="M2 4h12M4.5 8h7M6.5 12h3" />
                </svg>
            </button>

            {open && (
                <div className="absolute right-0 top-[calc(100%+6px)] z-40 w-[240px] rounded-2xl border border-border bg-background py-2 shadow-[0_18px_50px_-12px_rgba(20,23,29,0.25)]">
                    {!drill && (
                        <>
                            <div className="px-4 pb-1 pt-1.5 text-[12.5px] text-faint">
                                Filter by
                            </div>
                            {dimensions.map((dim) => (
                                <button
                                    key={dim.key}
                                    type="button"
                                    onClick={() => setDrill(dim)}
                                    className="flex w-full cursor-pointer items-center gap-2.5 border-0 bg-transparent px-4 py-[7px] text-left font-[inherit] text-[13.5px] text-foreground hover:bg-sunk"
                                >
                                    <span className="flex text-faint">
                                        {dim.icon}
                                    </span>
                                    <span className="flex-1">{dim.label}</span>
                                    <span
                                        aria-hidden
                                        className="text-[12px] text-faint"
                                    >
                                        ›
                                    </span>
                                </button>
                            ))}
                        </>
                    )}

                    {drill && (
                        <>
                            <div className="flex items-center gap-2 px-3 pb-1.5 pt-1">
                                <button
                                    type="button"
                                    aria-label="Back"
                                    onClick={() => {
                                        setDrill(null);
                                        setQuery('');
                                    }}
                                    className="cursor-pointer border-0 bg-transparent p-1 font-[inherit] text-[13px] text-faint hover:text-foreground"
                                >
                                    ‹
                                </button>
                                <span className="text-[13px] font-semibold">
                                    {drill.label}
                                </span>
                                <span className="ml-auto pr-1 text-[12px] text-faint">
                                    is
                                </span>
                            </div>
                            <div className="px-3 pb-2">
                                <SearchField
                                    autoFocus
                                    value={query}
                                    onChange={setQuery}
                                    placeholder="Search…"
                                    className="w-full"
                                />
                            </div>
                            <div className="max-h-[220px] overflow-y-auto">
                                {shownValues.map((value) => (
                                    <button
                                        key={value}
                                        type="button"
                                        onClick={() => {
                                            onAdd({
                                                key: drill.key,
                                                label: drill.label,
                                                value,
                                            });
                                            close();
                                        }}
                                        className="block w-full cursor-pointer truncate border-0 bg-transparent px-4 py-[7px] text-left font-[inherit] text-[13.5px] text-foreground hover:bg-sunk"
                                    >
                                        {value}
                                    </button>
                                ))}
                                {shownValues.length === 0 && (
                                    <div className="px-4 py-3 text-[12.5px] text-faint">
                                        No matches.
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
}
